import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { 
  ArrowRightIcon,
  CheckCircleIcon,
  CommandLineIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import SchemaMarkup from '../components/SchemaMarkup';

// Import the configuration
import { serviceConfig } from '../content/services';

export default function Services() {
  const { t, i18n } = useTranslation();
  const langPrefix = i18n.language === 'et' ? '/et' : '/en';
  const location = useLocation();
  
  // Handle incoming links (e.g. from Home page "Read More")
  const incomingTabIndex = location.state?.tabIndex;
  const [activeTab, setActiveTab] = useState(() => {
    return incomingTabIndex !== undefined ? incomingTabIndex : 0;
  });

  // Get text content
  const servicesText = t('services_page.tab_items', { returnObjects: true });
  const safeServicesText = Array.isArray(servicesText) ? servicesText : [];

  // Merge Config (Icons/Stack) with Text (Title/Desc)
  const activeService = safeServicesText[activeTab];
  const activeConfig = serviceConfig[activeTab];

return (
    <div className="bg-slate-50 min-h-screen pb-20">
      <SEO title={t('services_page.title')} description={t('services_page.meta_desc')} />
      
      <SchemaMarkup 
        type="service" 
        data={{
          name: t('services_page.title'),
          description: t('services_page.meta_desc'),
          url: `${langPrefix}/services`
        }} 
      />

      <PageHeader 
        title={t('services_page.title')} 
        subtitle={t('services_page.subtitle')} 
        maxWidth="max-w-6xl"
      />

      <div className="container mx-auto px-4 py-12 md:py-20 max-w-6xl">
        <div className="flex flex-col lg:flex-row bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden min-h-[700px]">
          
          {/* --- SIDEBAR --- */}
          <div className="w-full lg:w-1/3 bg-slate-900 p-4 md:p-6 text-white flex flex-col gap-2">
            {safeServicesText.map((service, index) => {
              // Safety fallback if config is missing
              const ConfigIcon = serviceConfig[index]?.icon || CommandLineIcon;
              const isActive = activeTab === index;
              
              return (
                <button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  className={`group w-full text-left p-4 rounded-xl transition-all duration-300 flex items-center gap-4 border-l-4 relative overflow-hidden
                    ${isActive 
                      ? 'bg-slate-800 border-blue-400 shadow-[inset_0_0_20px_rgba(37, 99, 235,0.1)]' 
                      : 'hover:bg-slate-800/50 border-transparent text-slate-400 hover:text-white'
                    }`}
                >
                  <ConfigIcon className={`h-6 w-6 flex-shrink-0 transition-colors ${isActive ? 'text-blue-400' : 'text-slate-500 group-hover:text-blue-200'}`} />
                  <div className="flex flex-col">
                    <span className={`font-bold text-sm lg:text-base leading-tight ${isActive ? 'text-white' : ''}`}>
                        {service.title}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* --- MAIN CONTENT AREA --- */}
          <div className="w-full lg:w-2/3 p-8 lg:p-12 flex flex-col bg-white relative">
            {activeService && (
              <div key={activeTab} className="animate-fadeIn h-full flex flex-col">
                
                {/* Header Section */}
                <div className="mb-8">
<h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 mb-3">
                        {activeService.title}
                    </h2>
                    {/* Tech Stack Chips */}
                    {activeConfig?.stack && (
                        <div className="flex flex-wrap gap-2 mb-8">
                            {activeConfig.stack.map(tech => (
                                <span key={tech} className="px-2 py-1 bg-slate-100 border border-slate-200 text-slate-600 rounded-md text-xs font-mono font-bold uppercase tracking-wide">
                                    {tech}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* The "Terminal" Description Box */}
                <div className="bg-slate-900 rounded-xl p-6 md:p-8 mb-10 border border-slate-800 shadow-lg relative overflow-hidden group">
                    {/* Decoration */}
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                         <CpuChipIcon className="w-24 h-24 text-white" />
                    </div>
                    
                    <p className="text-lg text-slate-300 leading-relaxed font-light relative z-10">
                      {activeService.full_desc}
                    </p>
                </div>
                
                {/* Capabilities List */}
                <div className="mb-12">
                   <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-6">
                        {t('services_page.ui.capabilities_label')}
                   </h3>
                   <div className="grid gap-4">
                       {(activeService.specs || []).map((spec, i) => (
                          <div key={i} className="flex gap-4 items-start group">
                            <div className="mt-1">
                                <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                            </div>
                            <span className="text-slate-700 font-medium leading-relaxed group-hover:text-slate-900 transition-colors">
                                {spec}
                            </span>
                          </div>
                        ))}
                   </div>
                </div>

                {/* CTA (Aligned to bottom) */}
                <div className="mt-auto pt-8 border-t border-slate-100 flex flex-wrap items-center gap-4">
                  <Link
                    to={`${langPrefix}/contact`}
                    className="group inline-flex items-center gap-3 bg-blue-600 text-white px-8 py-4 rounded-full font-bold shadow-lg hover:bg-blue-500 hover:shadow-xl hover:scale-105 transition-all duration-300"
                  >
                    {t('services_page.cta')}
                    <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  {activeConfig?.landingPath && (
                    <Link
                      to={`${langPrefix}${i18n.language === 'et' && activeConfig.etLandingPath ? activeConfig.etLandingPath : activeConfig.landingPath}`}
                      className="group inline-flex items-center gap-2 text-slate-600 font-semibold hover:text-blue-600 transition-colors"
                    >
                      {t('services_page.ui.details_link')}
                      <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  )}
                </div>

              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
